import { clsx } from 'clsx';
import React, { useEffect, useRef } from 'react';
import {
  AttributionControl as MapGlAttributionControl,
  Map as MapGlMap,
  useMap,
} from 'react-map-gl/maplibre';

import type { Coordinates } from '../../../../../types/frapi-types.js';
import type { MapMarker } from '../types.js';

import { useMapSizing } from '../hooks/useMapSizing.js';
import { TopListMapMarker } from './TopListMapMarker.js';

type TopListMapProps = {
  active: boolean;
  markers: MapMarker[];
  currentArticleId: string | null;
  onMarkerClick: (articleId: string) => void;
  initialLocation?: Coordinates;
  zoom?: number;
};

const OSLO_COORDS = { latitude: 59.9128, longitude: 10.7387 } as const;

const TopListMap: React.FC<TopListMapProps> = ({
  active,
  markers,
  currentArticleId,
  onMarkerClick,
  initialLocation = OSLO_COORDS,
  zoom = 12.5,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { ['top-list-map']: map } = useMap();

  useMapSizing(map, active, containerRef);

  useEffect(() => {
    if (!map || !currentArticleId) {
      return;
    }

    const marker = markers.find(
      (marker) => marker.articleId === currentArticleId,
    );

    if (!marker) {
      return;
    }

    const center: [number, number] = [
      marker.location.longitude,
      marker.location.latitude,
    ];

    map.flyTo({
      center,
      zoom,
      duration: 800,
      essential: true,
    });
  }, [map, markers, currentArticleId, zoom]);

  return (
    <div
      data-pulse-entity-id="top-list-map"
      ref={containerRef}
      aria-hidden={!active}
      className={clsx(
        'top-list__map-container',
        !active && 'top-list__map-container--hidden',
      )}
    >
      <MapGlMap
        id="top-list-map"
        initialViewState={{ zoom, ...initialLocation }}
        mapStyle="https://maps.vgc.no/api/maps/bright/style.json"
        attributionControl={false}
        logoPosition="top-left"
        interactive
      >
        <MapGlAttributionControl position="top-left" />
        {markers.map((marker) => (
          <TopListMapMarker
            key={marker.articleId}
            marker={marker}
            active={marker.articleId === currentArticleId}
            onClick={onMarkerClick}
          />
        ))}
      </MapGlMap>
    </div>
  );
};

export { TopListMap };
